import { call, put, takeEvery } from 'redux-saga/effects'
import { AsyncAction, Action } from 'src/utils/reduxUtils'
import {
  FetchDriver,
  SignInPhone,
  SignInEnterCode,
  SignInResendCode,
  SetAuthToken,
  Logout,
  SignUp,
  SetDriver,
  SetPartner,
  SetIsTokenSignIn,
} from 'src/constants/actionTypes'
import { ApiService, callApi } from './api/callApi'
import { Endpoints } from 'src/constants/endpoints'
import { authService } from 'src/services/AuthService'
import { ALREADY_REGISTERED_ERROR } from 'src/constants/auth'

interface SignUpRequest {
  partnerName: string
  phone: string
  email: string
  firstName: string
  lastName: string
  tagNames: string[]
  partnerMarketId: string
  onboardingTemplateId: string
  referral: string
}

function* onSignUp(action: AsyncAction<SignUpRequest>) {
  const {
    partnerName,
    phone,
    email,
    firstName,
    lastName,
    partnerMarketId,
    onboardingTemplateId,
    tagNames,
    referral,
  } = action.payload

  try {
    const { payload }: { payload: { smsSend: boolean } } = yield call(
      callApi,
      Endpoints.AuthSignUp,
      {
        method: 'POST',
        body: {
          phone,
          partnerName,
          email,
          firstName,
          lastName,
          partnerMarketId: partnerMarketId.trim(),
          onboardingTemplateId: onboardingTemplateId.trim(),
          tagNames,
          referral,
        },
        apiService: ApiService.Paraworks,
      },
    )

    if (payload?.smsSend === false) {
      throw {
        payload: {
          message: 'No partner driver found with the provided phone number',
        },
      }
    }

    yield put(SignUp.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const statusCode = err?.payload?.statusCode
    const errorMessage =
      statusCode === 409 ? ALREADY_REGISTERED_ERROR : err?.payload?.message || 'Failed to sign in'

    yield put(SignUp.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface SignInPhoneRequest {
  partnerName: string
  phone: string
}

function* onSignInPhone(action: AsyncAction<SignInPhoneRequest>) {
  const { partnerName, phone } = action.payload

  try {
    const { payload }: { payload: { smsSend: boolean } } = yield call(
      callApi,
      Endpoints.AuthSignIn,
      {
        method: 'POST',
        body: {
          phone,
          partnerName,
        },
        apiService: ApiService.Paraworks,
      },
    )

    if (payload?.smsSend === false) {
      throw {
        payload: {
          message: 'No partner driver found with the provided phone number',
        },
      }
    }

    yield put(SignInPhone.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage =
      typeof err?.payload?.message === 'string' ? err?.payload?.message : 'Failed to signIn'

    yield put(SignInPhone.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface SignInEnterCodeRequest {
  code: string
  partnerName: string
  phone: string
}

interface SignInEnterCodePayload {
  token: string
}

function* onSignInEnterCode(action: AsyncAction<SignInEnterCodeRequest>) {
  const { partnerName, code, phone } = action.payload

  try {
    const { payload }: { payload: SignInEnterCodePayload } = yield call(
      callApi,
      Endpoints.AuthValidateOtc,
      {
        method: 'POST',
        body: {
          code,
          partnerName,
          phone,
        },
        apiService: ApiService.Paraworks,
      },
    )

    yield put(SignInEnterCode.success())
    yield put(SetAuthToken.request(payload.token))
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage =
      typeof err?.payload?.message === 'string' ? err?.payload?.message : 'Failed to sign in'

    yield put(SignInPhone.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onSetDriverToken(action: Action<string>) {
  const authToken = action.payload

  yield put(SetAuthToken.success(authToken))

  authService.setStoredAuthToken(authToken)
}

function* onSetSignIn(action: Action<boolean>) {
  const isTokenSignIn = action.payload

  yield put(SetIsTokenSignIn.success(isTokenSignIn))

  authService.setStoredIsTokenSignIn(isTokenSignIn)
}

function* onLogout(action: AsyncAction) {
  try {
    yield put(SetDriver.request(null))
    yield put(SetPartner.request(null))
    yield put(SetAuthToken.request(null))
    yield put(SetIsTokenSignIn.request(null))

    // also clear legacy keys for users who haven't migrated yet
    authService.setStoredOnboardingToken(null)
    authService.setStoredRecollectionToken(null)

    yield put(Logout.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    yield put(Logout.failed(err))

    if (action.next) {
      action.next('Failed to logout')
    }
  }
}

export function* authSaga() {
  yield takeEvery([SignInPhone.type.REQUEST, SignInResendCode.type.REQUEST], onSignInPhone)
  yield takeEvery(SignInEnterCode.type.REQUEST, onSignInEnterCode)
  yield takeEvery(SignUp.type.REQUEST, onSignUp)
  yield takeEvery(SetAuthToken.type.REQUEST, onSetDriverToken)
  yield takeEvery(SetIsTokenSignIn.type.REQUEST, onSetSignIn)
  yield takeEvery(Logout.type.REQUEST, onLogout)
}
